import React, { useEffect, useRef } from "react";
import ReactTable from "react-table";
import { Link } from "react-router-dom";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import Assignment from "@material-ui/icons/Assignment";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import Button from "components/CustomButtons/Button.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import { GetTopUpMethodDescription, GetTopUpMethods } from "enums/TopUpMethod";
import { creditMonitoringApi, masterApi } from "functions/apis";
import { token } from "functions/Helper";

const useStyles = makeStyles({
  ...styles,
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px"
  }
});

export default function List() {
  const source = useRef(new AbortController());

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  const [currencyList, setCurrencyList] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [filter, setFilter] = React.useState({
    method: null,
    currencyID: null
  });
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (
      token["master.currencies.read"] === "allowed" ||
      token.is_admin === "True"
    )
      masterApi
        .get("/Currencies", {
          signal: source.current.signal,
          params: {
            pagination: {
              pageIndex: 0,
              pageSize: 0
            },
            filter: {
              searchString: null,
              publicationStatus: 1
            }
          }
        })
        .then(resp => setCurrencyList(resp.records))
        .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    setLoading(true);
    creditMonitoringApi
      .get("/TopUpLimitConfigurations", {
        signal: source.current.signal,
        params: {
          filter,
          pagination
        }
      })
      .then(resp => {
        setData(
          resp.records.map(record => ({
            ...record,
            methodDescription: GetTopUpMethodDescription(record.method),
            actions: (
              <div className="actions-right">
                {(token[
                  "creditmonitoring.top_up_limit_configurations.update"
                ] === "allowed" ||
                  token.is_admin === "True") && (
                  <Link
                    to={"/admin/TopUpLimitConfigurations/Edit/" + record.id}
                  >
                    <Button color="info" className="remove">
                      <EditIcon /> EDIT
                    </Button>
                  </Link>
                )}
                {(token[
                  "creditmonitoring.top_up_limit_configurations.delete"
                ] === "allowed" ||
                  token.is_admin === "True") && (
                  <Link
                    to={"/admin/TopUpLimitConfigurations/Delete/" + record.id}
                  >
                    <Button color="danger" className="remove">
                      <DeleteIcon /> DELETE
                    </Button>
                  </Link>
                )}
              </div>
            )
          }))
        );
        setPages(Math.ceil(resp.totalRecords / pagination.pageSize));
      })
      .catch(error => console.log(error))
      .finally(() => setLoading(false));
  }, [filter, pagination]);

  const classes = useStyles();

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Top Up Limit Configurations
            </h4>
          </CardHeader>
          <CardBody>
            {(token["creditmonitoring.top_up_limit_configurations.create"] ===
              "allowed" ||
              token.is_admin === "True") && (
              <Link to="/admin/TopUpLimitConfigurations/Create">
                <Button color="rose" type="button">
                  Create Top Up Limit Configuration
                </Button>
              </Link>
            )}
            <GridContainer>
              <GridItem xs={12} sm={6} md={4}>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <InputLabel
                    htmlFor="selMethod"
                    className={classes.selectLabel}
                  >
                    Method
                  </InputLabel>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu
                    }}
                    classes={{
                      select: classes.select
                    }}
                    value={filter.method === null ? "" : filter.method}
                    onChange={e =>
                      setFilter(prev => ({
                        ...prev,
                        method:
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value)
                      }))
                    }
                  >
                    <MenuItem
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected
                      }}
                      value=""
                    >
                      <b>All</b>
                    </MenuItem>
                    {GetTopUpMethods().map(record => (
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected
                        }}
                        value={record.key}
                        key={record.key}
                      >
                        <b>{record.description}</b>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6} md={4}>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <InputLabel
                    htmlFor="selCurrency"
                    className={classes.selectLabel}
                  >
                    Currency
                  </InputLabel>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu
                    }}
                    classes={{
                      select: classes.select
                    }}
                    value={filter.currencyID === null ? "" : filter.currencyID}
                    onChange={e =>
                      setFilter(prev => ({
                        ...prev,
                        currencyID:
                          e.target.value === "" ? null : e.target.value
                      }))
                    }
                  >
                    <MenuItem
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected
                      }}
                      value=""
                    >
                      <b>All</b>
                    </MenuItem>
                    {currencyList.map(record => (
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected
                        }}
                        value={record.id}
                        key={record.id}
                      >
                        <b>{record.code}</b>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  Header: "Method",
                  accessor: "methodDescription"
                },
                {
                  id: "currency",
                  Header: "Currency",
                  accessor: d => (d.currency ? d.currency.code : "-")
                },
                {
                  id: "hasMinimumLimit",
                  Header: "Has Minimum Limit",
                  accessor: d => (d.hasMinimumLimit ? "Yes" : "No")
                },
                {
                  id: "minimumLimit",
                  Header: "Minimum Limit",
                  accessor: d => d.minimumLimit
                },
                {
                  id: "hasMaximumLimit",
                  Header: "Has Maximum Limit",
                  accessor: d => (d.hasMaximumLimit ? "Yes" : "No")
                },
                {
                  id: "maximumLimit",
                  Header: "Maximum Limit",
                  accessor: d => d.maximumLimit
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
